import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from '../../PartnerOptions/constants'

import {
  addRecipientError,
  addRecipientSuccess,
  getCandidatesError,
  getCandidatesSuccess,
  getGeneralSettingsError,
  getGeneralSettingsSuccess,
  getRecipientsError,
  getRecipientsSuccess,
  getTagsError,
  getTagsSuccess,
  removeRecipientsError,
  removeRecipientsSuccess,
  updateGeneralSettingsError,
  updateGeneralSettingsSuccess,
} from './actions'
import {
  ADD_RECIPIENT_ASYNC,
  CANDIDATES_PER_PAGE,
  GENERAL_SETTINGS_REQUEST_ASYNC,
  GET_CANDIDATES_ASYNC,
  GET_RECIPIENTS_ASYNC,
  GET_TAGS_ASYNC,
  RECIPIENTS_PER_PAGE,
  REMOVE_RECIPIENT_ASYNC,
  SET_RECIPIENTS_PAGE,
  UPDATE_GENERAL_SETTINGS_REQUEST,
} from './constants'
import { selectCandidatesData, selectRecipientList, selectTagsData } from './selectors'

const getGeneralSettingsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      GENERAL_SETTINGS_REQUEST_ASYNC.INITIATE,
      UPDATE_GENERAL_SETTINGS_REQUEST.SUCCESS
    )
    .filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.simulator)
      return true
    })
    .mergeMap(() =>
      api.settings
        .getSimulatorSettings(store.getState().partnerOptions.selectedCompany?.value)
        .map((response) => getGeneralSettingsSuccess(response))
        .catch((error) => Observable.of(epicsErrorHandler(getGeneralSettingsError, error)))
        .takeUntil(action$.ofType(GENERAL_SETTINGS_REQUEST_ASYNC.CANCEL))
    )

const updateGeneralSettingsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(UPDATE_GENERAL_SETTINGS_REQUEST.INITIATE).mergeMap(() =>
    api.settings
      .updateSimulatorSettings(
        store.getState().partnerOptions.selectedCompany?.value,
        store.getState().form.SimulatorSettingsForm.values
      )
      .map((response) => updateGeneralSettingsSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(updateGeneralSettingsError, error, [400])))
      .takeUntil(action$.ofType(UPDATE_GENERAL_SETTINGS_REQUEST.CANCEL))
  )

const getTagsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(GET_TAGS_ASYNC.INITIATE, ADD_RECIPIENT_ASYNC.SUCCESS, REMOVE_RECIPIENT_ASYNC.SUCCESS)
    .mergeMap((action) => {
      const notDirectRequest = action.type !== GET_TAGS_ASYNC.INITIATE
      const page = notDirectRequest ? 1 : action.payload.params.page
      const search = notDirectRequest ? '' : action.payload.params.search

      return api.mailboxes
        .getTags({
          companyId: store.getState().partnerOptions.selectedCompany?.value,
          allTags: false,
          page,
          search,
        })
        .map((response) => getTagsSuccess(response))
        .catch((error) => Observable.of(epicsErrorHandler(getTagsError, error)))
        .takeUntil(action$.ofType(GET_TAGS_ASYNC.CANCEL))
    })

const getCandidatesEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      GET_CANDIDATES_ASYNC.INITIATE,
      ADD_RECIPIENT_ASYNC.SUCCESS,
      REMOVE_RECIPIENT_ASYNC.SUCCESS
    )
    .mergeMap((action) => {
      const notDirectRequest = action.type !== GET_CANDIDATES_ASYNC.INITIATE
      const page = notDirectRequest ? 1 : action.payload.params.page
      const search = notDirectRequest ? '' : action.payload.params.search

      return api.settings
        .getCandidates(
          store.getState().partnerOptions.selectedCompany?.value,
          page,
          CANDIDATES_PER_PAGE,
          search
        )
        .mergeMap((response) => Observable.of(getCandidatesSuccess(response)))
        .catch((error) => Observable.of(epicsErrorHandler(getCandidatesError, error, [400])))
        .takeUntil(action$.ofType(GET_CANDIDATES_ASYNC.CANCEL))
    })

const getRecipientsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(GET_RECIPIENTS_ASYNC.INITIATE, ADD_RECIPIENT_ASYNC.SUCCESS, SET_RECIPIENTS_PAGE)
    .mergeMap(() =>
      api.settings
        .getRecipients(
          store.getState().partnerOptions.selectedCompany?.value,
          selectRecipientList(store.getState()).page,
          RECIPIENTS_PER_PAGE
        )
        .mergeMap((response) => Observable.of(getRecipientsSuccess(response)))
        .catch((error) => Observable.of(epicsErrorHandler(getRecipientsError, error, [400])))
        .takeUntil(action$.ofType(GET_RECIPIENTS_ASYNC.CANCEL))
    )

const addRecipientEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(ADD_RECIPIENT_ASYNC.INITIATE).mergeMap(() =>
    api.settings
      .addRecipient(
        store.getState().partnerOptions.selectedCompany?.value,
        selectCandidatesData(store.getState()).value.map(({ value, type }) => ({
          id: value,
          type,
        })),
        selectTagsData(store.getState()).value
      )
      .mergeMap((response) => Observable.of(addRecipientSuccess(response)))
      .catch((error) => Observable.of(epicsErrorHandler(addRecipientError, error, [400])))
      .takeUntil(action$.ofType(ADD_RECIPIENT_ASYNC.CANCEL))
  )

const removeRecipientsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(REMOVE_RECIPIENT_ASYNC.INITIATE).mergeMap(() => {
    const selectionData = selectRecipientList(store.getState()).selectionData

    return api.settings
      .removeRecipients(
        store.getState().partnerOptions.selectedCompany?.value,
        selectionData.allItems
          ? {
              allItems: true,
              excludeIds: selectionData.selectionIds,
            }
          : {
              allItems: false,
              includeIds: selectionData.selectionIds,
              excludeIds: [],
            }
      )
      .mergeMap((response) => Observable.of(removeRecipientsSuccess(response)))
      .catch((error) => Observable.of(epicsErrorHandler(removeRecipientsError, error, [400])))
      .takeUntil(action$.ofType(REMOVE_RECIPIENT_ASYNC.CANCEL))
  })

export default combineEpics(
  getGeneralSettingsEpic,
  updateGeneralSettingsEpic,
  getTagsEpic,
  getCandidatesEpic,
  getRecipientsEpic,
  addRecipientEpic,
  removeRecipientsEpic
)
